import { defineStore } from "pinia"
import { startOfYear, endOfYear, getYear } from "date-fns"
import { fetchApiJson, postApiJson } from "@/services/repowerAPIService.js"

export default defineStore("invoicesStore", {
  state: () => ({
    dateRange: [startOfYear(new Date()), endOfYear(new Date())],
    invoices: [],
    loading: false,
    loadingFailed: false,
    invoiceEmail: "",
    iban: "",
    streetNameAndNumber: "",
    buildingApartment: "",
    postalCode: "",
    city: "",
    country: "",
    isInvoiceSettingsLoaded: false,
  }),
  actions: {
    setInvoiceEmail(email) {
      this.invoiceEmail = email ?? ""
    },
    setIban(iban) {
      this.iban = iban ?? ""
    },
    setInvoiceAddress(
      streetNameAndNumber,
      buildingApartment,
      postalCode,
      city,
      country,
    ) {
      this.streetNameAndNumber = streetNameAndNumber ?? ""
      this.buildingApartment = buildingApartment ?? ""
      this.postalCode = postalCode ?? ""
      this.city = city ?? ""
      this.country = country ?? ""
    },
    resetInvoiceSettings() {
      this.setInvoiceEmail()
      this.setIban()
      this.setInvoiceAddress()
    },
    loadInvoices(portfolioId, onSuccess, onFail) {
      this.loading = true
      this.loadingFailed = false
      return fetchApiJson(
        `/v2/portfolios/${portfolioId}/invoices/?year=${getYear(this.dateRange[0])}`,
      )
        .then((data) => {
          this.invoices = data
          onSuccess?.(data)
        })
        .catch((error) => {
          this.invoices = []
          this.loadingFailed = true
          onFail?.(error)
        })
        .finally(() => {
          this.loading = false
        })
    },
    getInvoicePdf(portfolioId, invoiceNumber, onSuccess, onFail) {
      return fetchApiJson(
        `/v2/portfolios/${portfolioId}/invoices/${invoiceNumber}/pdf/`,
      )
        .then((data) => {
          onSuccess?.(data)
        })
        .catch((error) => {
          onFail?.(error)
        })
    },
    loadInvoiceSettings(portfolioId, onSuccess, onFail) {
      this.isInvoiceSettingsLoaded = false
      return fetchApiJson(`/v2/portfolios/${portfolioId}/invoice-details/`)
        .then(({ data }) => {
          this.setInvoiceEmail(data.email)
          this.setIban(data.iban)
          this.setInvoiceAddress(
            data.address.street_number_addition,
            data.address.building_apartment,
            data.address.postal_code,
            data.address.city,
            data.address.country,
          )
          onSuccess?.(data)
        })
        .catch((error) => {
          this.resetInvoiceSettings()
          onFail?.(error)
        })
        .finally(() => {
          this.isInvoiceSettingsLoaded = true
        })
    },
    requestInvoiceEmailChange(portfolioId, newEmail, onSuccess, onFail) {
      return postApiJson(
        `/v2/portfolios/${portfolioId}/invoice-details/request-email-change/`,
        { invoice_email: newEmail },
        { method: "POST" },
      )
        .then((data) => {
          onSuccess?.(data)
        })
        .catch((error) => {
          onFail?.(error)
        })
    },
    changeInvoiceEmail(newEmail, token, password, onSuccess, onFail) {
      postApiJson(
        `/v2/portfolios/invoice-details/change-email/`,
        { token, password },
        { method: "PUT" },
      )
        .then(() => {
          this.invoiceEmail = newEmail
          onSuccess?.()
        })
        .catch((error) => {
          onFail?.(error)
        })
    },
    changeIban(portfolioId, newIban, onSuccess, onFail) {
      postApiJson(
        `/v2/portfolios/${portfolioId}/invoice-details/change-iban/`,
        { invoice_iban: newIban },
        { method: "PUT" },
      )
        .then(() => {
          this.iban = newIban
          onSuccess?.()
        })
        .catch((error) => {
          onFail?.(error)
        })
    },
    changeInvoiceAddress(portfolioId, address, onSuccess, onFail) {
      postApiJson(
        `/v2/portfolios/${portfolioId}/invoice-details/change-address/`,
        {
          street_number_addition: address.streetNameAndNumber,
          building_apartment: address.buildingApartment,
          postal_code: address.postalCode,
          city: address.city,
          country: address.country,
        },
        { method: "PUT" },
      )
        .then(() => {
          this.setInvoiceAddress(
            address.streetNameAndNumber,
            address.buildingApartment,
            address.postalCode,
            address.city,
            address.country,
          )
          onSuccess?.()
        })
        .catch((error) => {
          onFail?.(error)
        })
    },
  },
})
